// useFolderUpload.helpers.ts
import { addItemToSortedCache, normalizePath, shouldSkipCacheUpdatesWithLog } from '@qse/utils'
import { v4 as uuidv4 } from 'uuid'

// Validation function for folder names
const validateFolderName = (folderName: string): { valid: boolean; invalidChars: string[] } => {
  // eslint-disable-next-line no-control-regex
  const invalidChars = /[<>:"/\\|?\x00-\x1f]/g
  const foundChars = new Set<string>()
  let match

  while ((match = invalidChars.exec(folderName)) !== null) {
    // Handle control characters
    if (match[0].charCodeAt(0) <= 0x1f) {
      foundChars.add('control character')
    } else {
      foundChars.add(match[0])
    }
  }

  return {
    valid: foundChars.size === 0,
    invalidChars: Array.from(foundChars),
  }
}

// Recursively validate all folder names in the tree
const validateFolderTree = (
  node: { name: string; children?: any[] },
  path: string = '',
): { valid: boolean; folderName: string; invalidChars: string[] } | null => {
  const currentPath = path ? `${path}/${node.name}` : node.name
  const validation = validateFolderName(node.name)

  if (!validation.valid) {
    return {
      valid: false,
      folderName: currentPath,
      invalidChars: validation.invalidChars,
    }
  }

  // Check all children recursively
  if (node.children && node.children.length > 0) {
    for (const child of node.children) {
      const childValidation = validateFolderTree(child, currentPath)

      if (childValidation && !childValidation.valid) {
        return childValidation
      }
    }
  }

  return null
}

type ClientDirNode = {
  id?: string
  name: string
  children?: ClientDirNode[]
}

type CreatedFolderNode = {
  id: string
  name: string
  path: string
  folderStructure?: CreatedFolderNode[]
}

export type FolderInit = {
  isFolderUpload: true
  rootFolderId: string
  rootOriginalName: string
  serverName: string
  rootServerPath: string
  pathToId: Map<string, string>
  totalFiles: number
  totalBytes: number
  parentPath?: string
  clientRequestId?: string
}

type PickedResult = {
  rootName: string
  files: (File & { path?: string })[]
  // الشجرة من الجامع بدون IDs
  tree: { name: string; children?: PickedResult['tree'][] }
}

// مفاتيح التطبيع للماب
const pathKey = (p?: string) => {
  if (!p) return '/'
  let s = p.replace(/\\/g, '/').replace(/\/+/g, '/').trim()

  s = s
    .normalize('NFKC')
    .replace(/\u00A0|\u202F/g, ' ')
    .replace(/ +/g, ' ')
  s = s.replace(/[^\p{Letter}\p{Number} ./_\-/]+/gu, '').toLowerCase()
  if (!s.startsWith('/')) s = '/' + s
  if (s.length > 1 && s.endsWith('/')) s = s.slice(0, -1)

  return s
}

// Enhanced path matching function that handles numbered suffixes
export const findMatchingPath = (
  targetPath: string,
  availablePaths: Map<string, string>,
): string | undefined => {
  console.log('🔍 [findMatchingPath] Starting path matching:', {
    targetPath,
    availablePaths: Array.from(availablePaths.entries()),
    timestamp: new Date().toISOString(),
  })

  // First try exact match
  if (availablePaths.has(targetPath)) {
    console.log('🔍 [findMatchingPath] Found exact match:', targetPath)

    return availablePaths.get(targetPath)
  }

  // Try to find a path that matches when we remove the numbered suffix
  const targetParts = targetPath.split('/').filter(Boolean)
  const targetBase = targetParts[targetParts.length - 1] || ''
  const targetParent = targetParts.slice(0, -1).join('/')

  console.log('🔍 [findMatchingPath] Target analysis:', {
    targetParts,
    targetBase,
    targetParent,
    targetBaseClean: targetBase.replace(/\s*\(\d+\)$/, ''),
  })

  for (const [path, id] of availablePaths.entries()) {
    const pathParts = path.split('/').filter(Boolean)
    const pathBase = pathParts[pathParts.length - 1] || ''
    const pathParent = pathParts.slice(0, -1).join('/')

    // Check if the base names match (ignoring numbered suffixes like " (1)", " (2)", etc.)
    const targetBaseClean = targetBase.replace(/\s*\(\d+\)$/, '')
    const pathBaseClean = pathBase.replace(/\s*\(\d+\)$/, '')

    console.log('🔍 [findMatchingPath] Checking path:', {
      path,
      pathParts,
      pathBase,
      pathParent,
      pathBaseClean,
      targetBaseClean,
      baseNamesMatch: targetBaseClean === pathBaseClean,
      parentPathsMatch: targetParent === pathParent || (targetParent === '' && pathParent === ''),
    })

    if (targetBaseClean === pathBaseClean) {
      // More flexible parent path matching
      const parentMatches =
        targetParent === pathParent ||
        (targetParent === '' && pathParent === '') ||
        pathParent.endsWith('/' + targetParent) ||
        targetParent.endsWith('/' + pathParent) ||
        (targetParent === '' && pathParent !== '') ||
        (pathParent === '' && targetParent !== '')

      console.log('🔍 [findMatchingPath] Parent path analysis:', {
        targetParent,
        pathParent,
        parentMatches,
        pathParentEndsWithTarget: pathParent.endsWith('/' + targetParent),
        targetParentEndsWithPath: targetParent.endsWith('/' + pathParent),
      })

      if (parentMatches) {
        console.log('🔍 [findMatchingPath] Found match:', { path, id })

        return id
      }
    }
  }

  console.log('🔍 [findMatchingPath] No match found')

  return undefined
}

// 1) ولّد IDs لكل نود (جذر + children)
function assignIds(node: PickedResult['tree']): ClientDirNode {
  return {
    id: uuidv4(),
    name: node.name,
    children: (node.children || []).map(assignIds),
  }
}

// 2) ابنِ شجرة CreatedFolderNode مع paths كاملة (لاستخدامها لبناء map)
function toCreatedTreeWithPaths(
  node: ClientDirNode,
  basePath: string, // مثال: "/parent/RootName"
): CreatedFolderNode {
  const currentPath = normalizePath(`${basePath}/${node.name}`)

  return {
    id: node.id!, // مضمون بعد assignIds
    name: node.name,
    path: currentPath,
    folderStructure: (node.children || []).map(child => toCreatedTreeWithPaths(child, currentPath)),
  }
}

// 3) ابني map: path → id (يشمل الجذر + كل children)
function buildServerPathMap(rootCreated: CreatedFolderNode) {
  const map = new Map<string, string>()
  const add = (n: CreatedFolderNode) => {
    const pathKey1 = pathKey(n.path)
    const pathKey2 = pathKey('/' + n.name)

    map.set(pathKey1, n.id)
    map.set(pathKey2, n.id) // alias بالاسم
    n.folderStructure?.forEach(add)
  }

  add(rootCreated)

  return map
}

export type FolderInitiatePlaceholder = {
  clientRequestId: string
  rootName: string
  totalFiles: number
  totalBytes: number
  parentPath: string
}

export type InitiateFolderTreeError = {
  clientRequestId: string
  errorMessage: string
}

export type InitiateFolderTreeParams = {
  currentFolderId?: string
  parentPath: string
  picked: PickedResult
  queryClient?: any
  clientRequestId?: string
  onBeforeInitiate?: (placeholder: FolderInitiatePlaceholder) => void
  onInitiateError?: (payload: InitiateFolderTreeError) => void
}

export async function initiateFolderTree(
  params: InitiateFolderTreeParams,
): Promise<
  | { init: FolderInit; files: (File & { path?: string })[] }
  | { quotaExceeded: true; errors: string[] }
  | null
> {
  const {
    currentFolderId,
    parentPath,
    picked,
    queryClient,
    clientRequestId,
    onBeforeInitiate,
    onInitiateError,
  } = params

  console.log('🌳 [initiateFolderTree] Starting fresh folder tree initialization:', {
    currentFolderId,
    parentPath,
    timestamp: new Date().toISOString(),
  })

  if (!picked || !picked.rootName) {
    console.warn('🌳 [initiateFolderTree] No folder picked or invalid result:', picked)

    return null
  }

  const rootName = picked.rootName
  const onlyFiles = picked.files || []
  const totalFiles = onlyFiles.length
  const totalBytes = onlyFiles.reduce((s, f) => s + (f.size || 0), 0)

  console.log('🌳 [initiateFolderTree] Collected folder data:', {
    rootName,
    totalFiles,
    totalBytes,
    fileNames: onlyFiles.map(f => f.name),
    timestamp: new Date().toISOString(),
  })

  // Validate folder names before proceeding
  const rootValidation = validateFolderName(rootName)

  if (!rootValidation.valid) {
    const { toast } = await import('sonner')
    const invalidCharsFormatted = rootValidation.invalidChars.map(char => `"${char}"`).join(', ')

    toast.error(
      `The folder "${rootName}" contains invalid characters: ${invalidCharsFormatted}. ` +
        'Please rename the folder and try again. Invalid characters include: < > : " / \\ | ? and control characters.',
    )

    return null
  }

  // Validate all subfolder names in the tree
  if (picked.tree?.children) {
    for (const child of picked.tree.children) {
      const treeValidation = validateFolderTree(child, rootName)

      if (treeValidation && !treeValidation.valid) {
        const { toast } = await import('sonner')
        const invalidCharsFormatted = treeValidation.invalidChars
          .map(char => `"${char}"`)
          .join(', ')

        toast.error(
          `The folder "${treeValidation.folderName}" contains invalid characters: ${invalidCharsFormatted}. ` +
            'Please rename the folder and try again. Invalid characters include: < > : " / \\ | ? and control characters.',
        )

        return null
      }
    }
  }

  // (أ) ولّد ID للجذر، ثم IDs لكل children
  const rootClientId = clientRequestId || uuidv4()
  const clientTreeWithIds: ClientDirNode = {
    id: rootClientId,
    name: rootName,
    children: (picked.tree?.children || []).map(assignIds),
  }

  // Helper function to convert ClientDirNode to FolderNodeDto
  const convertToFolderNodeDto = (node: ClientDirNode): any => ({
    id: node.id!,
    name: node.name,
    children: (node.children || []).map(convertToFolderNodeDto),
  })

  // (ب) ابعث للشيرفر الشجرة بالـ IDs التي ولّدناها
  const folderData = {
    id: rootClientId, // 👈 مهم جدًا: نرسل نفس ID الذي سنستعمله محليًا
    name: rootName,
    parentId: currentFolderId,
    parentPath,
    totalFiles,
    totalSizeBytes: totalBytes,
    folderStructure: (clientTreeWithIds.children || []).map(convertToFolderNodeDto),
  }

  const { initiateFolderUpload } = await import('@qse/api')
  const { retryFolderUploadApi } = await import('@qse/utils')

  let result: { id?: string; name?: string; path?: string }

  // Trigger pre-initiate callback so UI can optimistically display the folder in tracker
  onBeforeInitiate?.({
    clientRequestId: rootClientId,
    rootName,
    totalFiles,
    totalBytes,
    parentPath,
  })

  try {
    result = await retryFolderUploadApi(() => initiateFolderUpload(folderData), {
      operation: 'initiateFolderUpload',
      folderId: rootClientId,
      fileName: rootName,
    })
  } catch (error: any) {
    console.error('🌳 [initiateFolderTree] Failed to initiate folder upload after retries:', error)

    // Extract error message from the response
    let errorMessage = 'Failed to initiate folder upload'

    if (error?.response?.data?.error?.message) {
      errorMessage = error.response.data.error.message
    } else if (error?.response?.data?.message) {
      errorMessage = error.response.data.message
    } else if (error?.message) {
      errorMessage = error.message
    }

    onInitiateError?.({
      clientRequestId: rootClientId,
      errorMessage,
    })

    // Show error toast to user
    const { toast } = await import('sonner')

    toast.error(errorMessage)

    // Return null to indicate failure
    return null
  }

  // (ج) حدّد قيم الجذر بعد الردّ
  const serverRootId = result.id || rootClientId // لو السيرفر بدّل ID الجذر، نلتقطه
  const serverName = result.name || rootName
  const rootBasePath = normalizePath(result.path || `${normalizePath(parentPath)}/${serverName}`)

  // (د) ابنِ شجرة CreatedFolderNode (بالمسارات) من نفس الشجرة التي فيها IDs التي أنشأناها
  const createdRoot: CreatedFolderNode = {
    id: serverRootId, // الجذر قد يختلف، نستخدم الذي رجع من السيرفر إن توفّر
    name: serverName,
    path: rootBasePath,
    folderStructure: (clientTreeWithIds.children || []).map(child =>
      toCreatedTreeWithPaths(child, rootBasePath),
    ),
  }

  // (هـ) ابنِ الـ map path→id الآن (ids مضمونة لأنها منّا)
  const pathToId = buildServerPathMap(createdRoot)

  // (و) Update cache with the newly created folder
  if (queryClient && !shouldSkipCacheUpdatesWithLog('both', 'folder creation')) {
    try {
      const { queryKeys } = await import('@qse/api')

      console.log('📂 [initiateFolderTree] Updating cache with folder:', {
        id: serverRootId,
        name: serverName,
        path: rootBasePath,
      })

      const filesListKey = queryKeys.files.lists()
      const queriesToUpdate = queryClient.getQueriesData({ queryKey: filesListKey })

      console.log('📂 [initiateFolderTree] Found queries to update:', queriesToUpdate.length)

      queriesToUpdate.forEach(([queryKey, cacheData]: [any, any]) => {
        if (!cacheData) return

        const folderToAdd = {
          id: serverRootId,
          name: serverName,
          path: rootBasePath,
          parentId: currentFolderId,
          isDir: true,
          status: 'UPLOADING', // Will be updated to ENCRYPTING then AVAILABLE via WebSocket
          isFavorite: false,
          mimeType: 'folder',
          mediaType: 'FOLDER',
          sizeBytes: totalBytes,
          effectivelyTrashed: false,
          effectivelyTrashedBy: null,
          trashedAt: null,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }

        // Handle infinite queries (pagination)
        if (cacheData.pages && Array.isArray(cacheData.pages)) {
          console.log('📂 [initiateFolderTree] Updating infinite query')
          const updatedPages = [...cacheData.pages]

          if (updatedPages[0]) {
            const firstPage = updatedPages[0]
            const folderExists = firstPage.data?.some(
              (f: any) => f.id === serverRootId || f.name === serverName,
            )

            if (!folderExists && firstPage.data) {
              const updatedFirstPage = addItemToSortedCache(firstPage, folderToAdd)

              updatedPages[0] = updatedFirstPage
              queryClient.setQueryData(queryKey, { ...cacheData, pages: updatedPages })
              console.log('📂 [initiateFolderTree] ✅ Added folder to first page')
            } else {
              console.log('📂 [initiateFolderTree] Folder already exists in cache')
            }
          }
        }
        // Handle regular queries
        else if (cacheData.data && Array.isArray(cacheData.data)) {
          console.log('📂 [initiateFolderTree] Updating regular query')
          const folderExists = cacheData.data.some(
            (f: any) => f.id === serverRootId || f.name === serverName,
          )

          if (!folderExists) {
            queryClient.setQueryData(queryKey, addItemToSortedCache(cacheData, folderToAdd))
            console.log('📂 [initiateFolderTree] ✅ Added folder to regular query')
          } else {
            console.log('📂 [initiateFolderTree] Folder already exists in cache')
          }
        }
      })
    } catch (e) {
      console.warn('[initiateFolderTree] Cache update failed', e)
    }
  }

  const init: FolderInit = {
    isFolderUpload: true,
    rootFolderId: serverRootId,
    rootOriginalName: rootName,
    serverName,
    rootServerPath: rootBasePath,
    pathToId,
    totalFiles,
    totalBytes,
    parentPath,
    clientRequestId: rootClientId,
  }

  return { init, files: onlyFiles }
}
